import React, { useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import {
    useGetCourseByIdQuery,
    useUpdateCourseMutation,
    useUploadToS3Mutation
} from '../../store/services/university-service';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
    Typography,
    Switch,
    Tooltip,
    IconButton,
    CardMedia,
    Chip,
    TextField
} from "@mui/material";
import { APP_URL, COURSE_CATEGORIES, SIM_AND_COURSE_FORMATS } from "../../config";
import { useTheme } from "@mui/material/styles";
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import MainCard from "../../components/cards/MainCard";
import { getCardColor } from "../../utils/colorUtils";
import HelpIcon from "@mui/icons-material/HelpOutline";
import TrimmedTextField from "../../components/TrimmedTextField";


CourseEdit.propTypes = {
    courseId: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default function CourseEdit({ courseId, onSave, onClose }) {
    const [editedCourseData, setEditedCourseData] = useState(null);
    const [isRandomSend, setIsRandomSend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadStates, setUploadStates] = useState({
        content: { isUploading: false, progress: 0 },
        image: { isUploading: false, progress: 0 },
        gif: { isUploading: false, progress: 0 }
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const fileInputRefs = {
        content: useRef(null),
        image: useRef(null),
        gif: useRef(null)
    };

    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    const { data: courseData, isLoading: courseLoading, error: courseError } = useGetCourseByIdQuery({
        courseId: courseId,
        includes: '',
    });

    const course_gif = courseData ? `${APP_URL}/${courseData.courseContentPath.replace('index.html', 'card.gif')}` : '';
    const course_img = courseData ? `${APP_URL}/${courseData.courseContentPath.replace('index.html', 'card.png')}` : '';

    const [updateCourse] = useUpdateCourseMutation();
    const [uploadToS3] = useUploadToS3Mutation();

    const courseLanguageCodeOptions = COURSE_CATEGORIES.split(',');
    const courseFormatOptions = SIM_AND_COURSE_FORMATS.split(',');
    // Topical category (distinct from the LMS/SCORM technical type). Drives the
    // reports-b "Course Type" column. Kept in sync with reports-b COURSE_TYPES.
    const courseTagOptions = ['Cybersecurity', 'Information Security', 'Workplace Practices', 'Policies', 'Compliance'];

    const hasContentPath = editedCourseData?.courseContentPath && editedCourseData.courseContentPath.length > 0;

    useEffect(() => {
        if (courseData) {
            setEditedCourseData({
                ...courseData,
                randomSend: courseData.randomSend ?? false,
                sharedTenancyIds: courseData.sharedTenancyIds ?? [],
                publishToFamily: courseData.publishToFamily ?? false,
                languageCodes: courseData.languageCodes
                    ? (Array.isArray(courseData.languageCodes) ? courseData.languageCodes : [courseData.languageCodes])
                    : (courseData.languageCode ? [courseData.languageCode] : [])
            });
            setIsRandomSend(courseData.randomSend ?? false);
        }
    }, [courseData]);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const updatedData = {
                ...editedCourseData,
                randomSend: Boolean(isRandomSend),
                universityCourseId: courseId
            };
            const response = await updateCourse({ courseData: updatedData }).unwrap();
            if (response && response.message === "Ok") {
                if (response.course && response.course.randomSend !== isRandomSend) {
                    setIsRandomSend(response.course.randomSend);
                }
                onSave(response.course || updatedData);
                showSnackbar("Course updated successfully", "success");
            } else {
                throw new Error("Unexpected response from server");
            }
        } catch (error) {
            console.error("Error in handleSave:", error);
            setError(error.message || "An error occurred while updating the course");
            showSnackbar(error.message || "An error occurred while updating the course", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event, fileType) => {
        const file = event.target.files[0];
        if (file) {
            handleUploadContent(file, fileType);
        }
    };

    const handleRandomSendChange = (event) => {
        setIsRandomSend(event.target.checked);
    };

    const handleSharedTenanciesChange = (tenancyIds) => {
        setEditedCourseData(prevData => ({
            ...prevData,
            sharedTenancyIds: tenancyIds
        }));
    };

    const handlePublishToFamilyChange = (event) => {
        setEditedCourseData(prevData => ({
            ...prevData,
            publishToFamily: event.target.checked
        }));
    };

    const fileTypes = {
        content: "application/zip,application/x-zip-compressed",
        image: "image/png",
        gif: "image/gif"
    };

    const handleUploadContent = async (file, fileType) => {
        setUploadStates(prev => ({
            ...prev,
            [fileType]: { isUploading: true, progress: 0 }
        }));

        const contentTypes = {
            content: ["application/zip", "application/x-zip-compressed", "application/octet-stream"],
            image: ["image/png"],
            gif: ["image/gif"]
        };

        if (fileType === 'content') {
            const isZipFile = file.name.toLowerCase().endsWith('.zip') &&
                (contentTypes[fileType].includes(file.type) || file.type === "");
            if (!isZipFile) {
                showSnackbar(`Invalid file type. Please upload a ZIP file.`, "error");
                resetUploadState(fileType);
                return;
            }
        } else if (!contentTypes[fileType].includes(file.type)) {
            showSnackbar(`Invalid file type. Please upload a ${contentTypes[fileType][0]} file.`, "error");
            resetUploadState(fileType);
            return;
        }

        try {
            const response = await uploadToS3({
                fileType: contentTypes[fileType][0],
                id: courseId,
                file,
                onProgress: (progress) => updateUploadProgress(fileType, progress)
            }).unwrap();

            if (response.success) {
                showSnackbar(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} Uploaded Successfully.`, "success");
                refreshMedia();
            } else {
                throw new Error(`Error uploading ${fileType}.`);
            }
        } catch (error) {
            console.error(`Error uploading ${fileType}:`, error);
            showSnackbar(`Error uploading ${fileType}: ${error.message}`, "error");
        } finally {
            resetUploadState(fileType);
        }
    };

    const updateUploadProgress = (fileType, progress) => {
        setUploadStates(prev => ({
            ...prev,
            [fileType]: { ...prev[fileType], progress }
        }));
    };

    const resetUploadState = (fileType) => {
        setUploadStates(prev => ({
            ...prev,
            [fileType]: { isUploading: false, progress: 0 }
        }));
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const [mediaTimestamp, setMediaTimestamp] = useState(Date.now());

    const refreshMedia = () => {
        setTimeout(() => {
            setMediaTimestamp(Date.now());
        }, 4000); // 4-second delay
    };

    const getTimestampedUrl = (url) => {
        return `${url}?ts=${mediaTimestamp}`;
    };

    const renderUploadButton = (fileType, acceptedTypes) => {
        const isDisabled = (fileType === 'image' || fileType === 'gif') && !hasContentPath;
        const button = (
            <Button
                variant="contained"
                color="secondary"
                sx={{
                    width: '100%',
                    height: '40px',
                }}
                onClick={() => fileInputRefs[fileType].current.click()}
                disabled={uploadStates[fileType].isUploading || isDisabled}
            >
                {uploadStates[fileType].isUploading ? (
                    <CircularProgress size={24} variant="determinate" value={uploadStates[fileType].progress} />
                ) : (
                    `Upload ${fileType.charAt(0).toUpperCase() + fileType.slice(1)}`
                )}
            </Button>
        );

        if (isDisabled) {
            return (
                <Tooltip title="Please upload course content first" placement="top" arrow>
                    <span style={{ flex: 1, width: '100%' }}>{button}</span>
                </Tooltip>
            );
        }

        return (
            <React.Fragment>
                {button}
                <input
                    type="file"
                    ref={fileInputRefs[fileType]}
                    style={{ display: "none" }}
                    accept={acceptedTypes}
                    onChange={(e) => handleFileChange(e, fileType)}
                />
            </React.Fragment>
        );
    };

    if (courseLoading) {
        return <CircularProgress />;
    }

    if (courseError) {
        return <div>Error loading course: {courseError.message}</div>;
    }

    if (!editedCourseData) {
        return null;
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TrimmedTextField
                    label="Course Name"
                    value={editedCourseData.courseName}
                    fullWidth
                    onChange={(e) => setEditedCourseData({ ...editedCourseData, courseName: e.target.value })}
                    sx={{ mb: 2 }}
                    disabled={true}
                />
                <TrimmedTextField
                    label="Scored Out Of"
                    value={editedCourseData.scoredOutOf}
                    fullWidth
                    onChange={(e) => setEditedCourseData({ ...editedCourseData, scoredOutOf: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TrimmedTextField
                    label="Course Content Path"
                    value={editedCourseData.courseContentPath}
                    fullWidth
                    onChange={(e) => setEditedCourseData({ ...editedCourseData, courseContentPath: e.target.value })}
                    sx={{ mb: 2 }}
                    disabled={true}
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Autocomplete
                        id="language-code"
                        multiple
                        options={courseLanguageCodeOptions}
                        value={editedCourseData.languageCodes || []}  // CHANGE THIS
                        onChange={(_, newValue) => setEditedCourseData({ ...editedCourseData, languageCodes: newValue || [] })}
                        renderInput={(params) => <TextField {...params} label="Languages" />}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={option}
                                />
                            ))
                        }
                        sx={{ flex: 1 }}
                    />
                    <Autocomplete
                        id="format-type"
                        options={courseFormatOptions}
                        value={editedCourseData.format}
                        onChange={(_, newValue) => setEditedCourseData({ ...editedCourseData, format: newValue })}
                        renderInput={(params) => <TrimmedTextField {...params} label="Format Type" />}
                        sx={{ flex: 1 }}
                    />
                    <Autocomplete
                        id="course-tag"
                        options={courseTagOptions}
                        value={editedCourseData.tag || null}
                        onChange={(_, newValue) => setEditedCourseData({ ...editedCourseData, tag: newValue || '' })}
                        renderInput={(params) => <TrimmedTextField {...params} label="Category" />}
                        sx={{ flex: 1 }}
                    />
                </Box>

                {/* Visibility Card */}
                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{
                        mb: 3,
                        bgcolor: getCardColor(lightMode, courseData.published)
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h4">Course Visibility</Typography>
                            <Tooltip title="When enabled, the course will be visible to users according to sharing settings" placement="top">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Switch
                            checked={editedCourseData.published}
                            onChange={(e) => setEditedCourseData({ ...editedCourseData, published: e.target.checked })}
                            inputProps={{ "aria-label": "course visibility" }}
                        />
                    </Box>
                    <Typography variant="caption">
                        {editedCourseData.published
                            ? "The course is visible to users according to sharing settings"
                            : "The course is currently hidden from all users"}
                    </Typography>
                </MainCard>

                {/* Course Sharing Card */}
                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                >
                    <Typography variant="h4" sx={{ mb: 2 }}>Course Sharing Settings</Typography>

                    {/* Tenancy Selection Component */}
                    <TenancySelectionComponent
                        selectedTenancies={editedCourseData.sharedTenancyIds || []}
                        onChange={handleSharedTenanciesChange}
                    />

                    {/* Publish to Family Switch */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1,
                        mb: 1,
                        bgcolor: getCardColor(lightMode),
                        borderRadius: 1
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body1">Share with Tenancy Family</Typography>
                            <Tooltip title="When enabled, this course will be accessible to all descendants of the selected tenancies" placement="top">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Switch
                            checked={editedCourseData.publishToFamily || false}
                            onChange={handlePublishToFamilyChange}
                            inputProps={{ "aria-label": "publish to family" }}
                            disabled={editedCourseData.sharedTenancyIds?.includes(0)}
                        />
                    </Box>

                    {editedCourseData.sharedTenancyIds?.includes(0) && (
                        <Typography variant="caption">
                            "Family sharing" is disabled because the course is shared with all tenancies.
                        </Typography>
                    )}

                </MainCard>

                {/* Course Media Section */}
                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                >
                    <Typography variant="h4" gutterBottom>Course Media</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <MainCard
                            content={false}
                            boxShadow={false}
                            border={true}
                            sx={{ flex: 1, mr: 1, display: 'flex', flexDirection: 'column' }}
                        >
                            <CardMedia
                                component="img"
                                image={getTimestampedUrl(course_img)}
                                alt="Course Image"
                                sx={{ objectFit: 'contain', maxHeight: 200 }}
                            />
                            <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" align="center">Course Image</Typography>
                            </Box>
                        </MainCard>
                        <MainCard
                            content={false}
                            boxShadow={false}
                            border={true}
                            sx={{ flex: 1, ml: 1, display: 'flex', flexDirection: 'column' }}
                        >
                            <CardMedia
                                component="img"
                                image={getTimestampedUrl(course_gif)}
                                alt="Course GIF"
                                sx={{ objectFit: 'contain', maxHeight: 200 }}
                            />
                            <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" align="center">Course GIF</Typography>
                            </Box>
                        </MainCard>
                    </Box>

                    {/* Upload Buttons */}
                    <Box sx={{
                        display: "flex",
                        gap: 2,
                        mb: 2,
                        '& > *': {
                            flex: 1,
                            width: '33.33%'
                        }
                    }}>
                        {Object.entries(fileTypes).map(([fileType, acceptedTypes]) => (
                            <React.Fragment key={fileType}>
                                {renderUploadButton(fileType, acceptedTypes)}
                            </React.Fragment>
                        ))}
                    </Box>
                </MainCard>

                {/* Save Button */}
                <Box>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="warning"
                        fullWidth
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Grid>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Grid>
    );
}